import { Link } from 'react-router'
import { Check, Pause } from 'lucide-react'
import { useHitTarget } from '../../platform/responsive/hitTarget'
import { ButtonLink } from '../../platform/primitives/ButtonLink'
// Reuses the shared pre-login shell's brand block — see AuthCard.tsx. Not the
// component itself: AuthCard always renders its title/subtitle as the FIRST
// thing inside the card, and this screen needs a status row (the badge and
// the return estimate) above the heading, which AuthCard has no slot for.
// Duplicating the three-node brand markup under its own existing classes
// avoids inventing a second brand lockup while staying inside AuthCard's own
// prop shape. `auth.css` is not edited — only imported, exactly as
// `SessionExpired.tsx` and every other screen using these classes already do.
import '../auth/auth.css'
import './errors.css'

/**
 * One row of the "what still works" list — a per-capability status the
 * backend reports (e.g. reads keep working while writes are paused).
 */
export interface MaintenanceCapability {
  /** e.g. "Reading your data" */
  label: string
  /** The word the backend wants shown, e.g. "Working" or "Paused" — never
   *  invented here, so a status this component has not been told about still
   *  renders correctly instead of being silently relabelled. */
  statusLabel: string
  /** Drives the dot's fill/glyph: true renders the positive check on
   *  `--pos`, false the paused glyph on `--warn`. */
  available: boolean
}

export interface MaintenanceProps {
  /** The screen's own headline, e.g. "We're upgrading the sync engine". */
  heading: string
  /** The explanatory paragraph. Whatever the backend sends is rendered
   *  as-is — this component does not compose or embellish it. */
  body: string
  /**
   * A preformatted return estimate, e.g. "Back at 03:30 PDT" — a timezone and
   * a clock reading only the backend can produce. Omit while unknown; nothing
   * about a return time renders without it, rather than a placeholder.
   */
  returnEta?: string
  /**
   * Per-capability status. Omit if the backend has no such breakdown to
   * report — the list is not shown with a default set of rows in its place.
   */
  capabilities?: MaintenanceCapability[]
  /**
   * The status page's URL. Omit if there isn't one; "Status page" is not
   * shown pointing nowhere.
   */
  statusUrl?: string
}

/**
 * "Planned maintenance" — a full-page notice for when the backend reports it
 * is intentionally down, not an error screen for when it merely failed.
 *
 * **No maintenance trigger exists yet.** There is no 503 handling in
 * `api/client.ts`, no maintenance flag and no status endpoint anywhere in
 * this app (checked before writing this file). So every value the comp draws
 * — the "Back at 03:30 PDT" estimate, "Roughly forty minutes", the three
 * capability rows, all of it — is data this client does not have and cannot
 * invent. This component takes that data as props instead of rendering any
 * of the comp's own figures, and renders each optional part only when it is
 * actually supplied. `MaintenanceProps` is the contract the caller — and
 * eventually a real backend — has to satisfy; see the props above.
 *
 * **Two things the comp draws are omitted outright, not made optional.**
 *
 * "Continue read-only" is not built: a read-only mode does not exist
 * anywhere in this client, and a button offering to "continue" into a mode
 * the app cannot enter would be inventing a feature rather than describing
 * one. `body` may still say the account is read-only during the outage —
 * that is the backend's prose, passed through, not a claim this component
 * makes about its own behaviour.
 *
 * "Status page" is not omitted the same way — the CONTROL is real, once
 * given a URL — but with no URL it renders nothing rather than a link to
 * nowhere, via `statusUrl`.
 */
export function Maintenance({ heading, body, returnEta, capabilities, statusUrl }: MaintenanceProps) {
  // Same reasoning as AuthCard's own brand block: the mark stays 30px and the
  // anchor grows around it so the tap target clears M0-R29 without redrawing
  // the design. See AuthCard.tsx.
  const brandMinHeight = useHitTarget('page')

  return (
    <div className="auth-page">
      <div className="auth-shell">
        <Link to="/" className="auth-brand" aria-label="TrackE5 home" style={{ minHeight: brandMinHeight }}>
          <span className="auth-brand__mark" aria-hidden="true">
            t5
          </span>
          <span className="auth-brand__word">trackE5</span>
        </Link>

        <div className="maint-card">
          <div className="maint-status">
            {/* Static copy — this is what kind of notice the screen is, not a
                figure the backend supplies, so it always renders. */}
            <span className="maint-badge">Planned maintenance</span>
            {returnEta ? <span className="maint-eta">{returnEta}</span> : null}
          </div>

          <div className="maint-head">
            <h1 className="maint-heading">{heading}</h1>
            <p className="maint-body">{body}</p>
          </div>

          {capabilities && capabilities.length > 0 ? (
            <ul className="maint-status-list">
              {capabilities.map((capability) => (
                <li className="maint-status-row" key={capability.label}>
                  <span
                    className={
                      capability.available
                        ? 'maint-status-dot maint-status-dot--ok'
                        : 'maint-status-dot maint-status-dot--paused'
                    }
                    aria-hidden="true"
                  >
                    {capability.available ? (
                      <Check size={11} strokeWidth={3} />
                    ) : (
                      <Pause size={9} strokeWidth={3} />
                    )}
                  </span>
                  <span className="maint-status-label">{capability.label}</span>
                  <span
                    className={
                      capability.available ? 'maint-status-value maint-status-value--ok' : 'maint-status-value maint-status-value--paused'
                    }
                  >
                    {capability.statusLabel}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}

          {statusUrl ? (
            <div className="maint-actions">
              <ButtonLink
                href={statusUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="chip"
              >
                Status page
              </ButtonLink>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
